import { useCallback, useEffect, useMemo, useState } from 'react';

export interface Selection<T extends { id: string }> {
  handleDeselectAll: () => void;
  handleDeselectCurrPage: () => void;
  handleDeselectOne: (item: T) => void;
  handleSelectOne: (item: T) => void;
  handleSelectCurrPage: () => void;
  handleSelectAll: () => void;
  currPageSelected: boolean;
  allSelected: boolean;
  selected: T[];
}

export const useSelection = <T extends { id: string },>(items: T[] = []): Selection<T> => {
  const [selected, setSelected] = useState<T[]>([]);
  // all includes items not present in the current page
  const [allSelected, setAllSelected] = useState(false);
  const currPageSelected = useMemo(() => selected.length > 0 && selected.length === items.length, [items, selected])

  useEffect(
    // when new items are passed into the hook, either user enters the page or the user flips page
    () => {
      if (allSelected) {
        // user opens page for first time or flips page, and selectAll is true, select the current page's item
        setSelected(items)
      }
    },
    [items]
  );

  const handleSelectOne = useCallback((item: T): void => setSelected((prevState) => [...prevState, item]), []);

  const handleSelectCurrPage = useCallback(() => setSelected([...items]), [items])

  const handleSelectAll = useCallback(
    (): void => {
      setSelected(items)
      setAllSelected(true)
    }, [setAllSelected, items]
  );

  const handleDeselectOne = useCallback(
    (item: T): void => {
      setSelected((prevState) => {
        return prevState.filter((_item) => _item.id !== item.id);
      });
      setAllSelected(false)
    },
    [setSelected]
  );

  const handleDeselectCurrPage = useCallback(() => {
    setSelected([])
    setAllSelected(false)
  }, [setSelected]);

  const handleDeselectAll = useCallback(
    () => {
      setSelected([]);
      setAllSelected(false)
    },
    [setAllSelected, setSelected]
  );

  return {
    handleDeselectAll,
    handleDeselectOne,
    handleDeselectCurrPage,
    handleSelectAll,
    handleSelectOne,
    handleSelectCurrPage,
    allSelected,
    selected,
    currPageSelected
  };
};
